import { useState, useEffect, useCallback } from 'react';
import { Link, router } from '@inertiajs/react';
import type { HexLibrary, HexLibraryBook, HexLibraryChapter, HexLibraryPage } from './types';

interface Props {
    library: HexLibrary;
    currentPageId: number | null;
    hexId: number;
    canEdit: boolean;
    isMobile: boolean;
    closeSidebar: () => void;
    onShowNewBookModal: () => void;
    onShowNewChapterModal: (bookId: number) => void;
    onShowNewPageModal: (chapterId: number) => void;
    onShowEditBookModal: (book: HexLibraryBook) => void;
    onShowEditChapterModal: (chapter: HexLibraryChapter, bookId: number) => void;
    /** Custom URL builder for page links (default: view page URL) */
    pageHrefBuilder?: (hexId: number, pageId: number) => string;
}

/**
 * HexLibrarySidebar - Displays the hierarchical book/chapter/page structure
 * for a hex's library. Features mini book spines that expand to show chapters.
 */
export function HexLibrarySidebar({
    library,
    currentPageId,
    hexId,
    canEdit,
    isMobile,
    closeSidebar,
    onShowNewBookModal,
    onShowNewChapterModal,
    onShowNewPageModal,
    onShowEditBookModal,
    onShowEditChapterModal,
    pageHrefBuilder,
}: Props) {
    const [expandedBooks, setExpandedBooks] = useState<Set<number>>(new Set());
    const [expandedChapters, setExpandedChapters] = useState<Set<number>>(new Set());

    // Auto-expand the book/chapter containing the current page
    useEffect(() => {
        if (currentPageId) {
            library.forEach(book => {
                book.chapters.forEach(chapter => {
                    if (chapter.pages.some(p => p.id === currentPageId)) {
                        setExpandedBooks(prev => new Set([...prev, book.id]));
                        setExpandedChapters(prev => new Set([...prev, chapter.id]));
                    }
                });
            });
        }
    }, [currentPageId, library]);

    const toggleBook = useCallback((bookId: number) => {
        setExpandedBooks(prev => {
            const next = new Set(prev);
            if (next.has(bookId)) {
                next.delete(bookId);
            } else {
                next.add(bookId);
            }
            return next;
        });
    }, []);

    const toggleChapter = useCallback((chapterId: number) => {
        setExpandedChapters(prev => {
            const next = new Set(prev);
            if (next.has(chapterId)) {
                next.delete(chapterId);
            } else {
                next.add(chapterId);
            }
            return next;
        });
    }, []);

    const handlePageClick = useCallback((pageId: number) => {
        if (isMobile) {
            closeSidebar();
        }
    }, [isMobile, closeSidebar]);

    const handleDeleteBook = useCallback((bookId: number, bookTitle: string) => {
        if (confirm(`Are you sure you want to delete "${bookTitle}"? This will delete all chapters and pages within it.`)) {
            router.delete(`/hex/${hexId}/books/${bookId}`);
        }
    }, [hexId]);

    const handleDeleteChapter = useCallback((bookId: number, chapterId: number, chapterTitle: string) => {
        if (confirm(`Are you sure you want to delete "${chapterTitle}"? This will delete all pages within it.`)) {
            router.delete(`/hex/${hexId}/books/${bookId}/chapters/${chapterId}`);
        }
    }, [hexId]);

    if (library.length === 0 && !canEdit) {
        return (
            <div className="p-4">
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    Library
                </h2>
                <p className="text-gray-500 text-sm">No books in this library yet.</p>
            </div>
        );
    }

    return (
        <div className="p-4">
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Library
            </h2>

            {/* Mini bookshelf */}
            <div className="space-y-2">
                {library.map(book => (
                    <BookSpine
                        key={book.id}
                        book={book}
                        hexId={hexId}
                        isExpanded={expandedBooks.has(book.id)}
                        onToggle={() => toggleBook(book.id)}
                        expandedChapters={expandedChapters}
                        onToggleChapter={toggleChapter}
                        currentPageId={currentPageId}
                        onPageClick={handlePageClick}
                        canEdit={canEdit}
                        onEditBook={() => onShowEditBookModal(book)}
                        onDeleteBook={() => handleDeleteBook(book.id, book.title)}
                        onAddChapter={() => onShowNewChapterModal(book.id)}
                        onEditChapter={(chapter) => onShowEditChapterModal(chapter, book.id)}
                        onDeleteChapter={(chapterId, title) => handleDeleteChapter(book.id, chapterId, title)}
                        onAddPage={onShowNewPageModal}
                        isOnlyBook={library.length === 1}
                        pageHrefBuilder={pageHrefBuilder}
                    />
                ))}
            </div>

            {/* Add Book button */}
            {canEdit && (
                <button
                    onClick={onShowNewBookModal}
                    className="mt-4 w-full px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-slate-700/50 rounded-lg border border-dashed border-slate-600 hover:border-slate-500 transition-colors flex items-center justify-center gap-2"
                >
                    <span>+</span>
                    <span>Add Book</span>
                </button>
            )}
        </div>
    );
}

interface BookSpineProps {
    book: HexLibraryBook;
    hexId: number;
    isExpanded: boolean;
    onToggle: () => void;
    expandedChapters: Set<number>;
    onToggleChapter: (id: number) => void;
    currentPageId: number | null;
    onPageClick: (pageId: number) => void;
    canEdit: boolean;
    onEditBook: () => void;
    onDeleteBook: () => void;
    onAddChapter: () => void;
    onEditChapter: (chapter: HexLibraryChapter) => void;
    onDeleteChapter: (chapterId: number, title: string) => void;
    onAddPage: (chapterId: number) => void;
    /** Whether this is the only book (can't be deleted) */
    isOnlyBook: boolean;
    /** Custom URL builder for page links */
    pageHrefBuilder?: (hexId: number, pageId: number) => string;
}

/**
 * BookSpine - A horizontal book spine that expands to show chapters.
 * Uses the book's hue value for accent colors.
 */
function BookSpine({
    book,
    hexId,
    isExpanded,
    onToggle,
    expandedChapters,
    onToggleChapter,
    currentPageId,
    onPageClick,
    canEdit,
    onEditBook,
    onDeleteBook,
    onAddChapter,
    onEditChapter,
    onDeleteChapter,
    onAddPage,
    isOnlyBook,
    pageHrefBuilder,
}: BookSpineProps) {
    // Generate colors from hue
    const accentColor = `hsl(${book.hue}, 50%, 35%)`;
    const accentColorDark = `hsl(${book.hue}, 40%, 25%)`;
    const accentColorVeryDark = `hsl(${book.hue}, 35%, 18%)`;

    return (
        <div className="rounded-lg overflow-hidden">
            {/* Book spine header - clickable to expand */}
            <div
                className="group relative flex items-center transition-colors cursor-pointer"
                style={{
                    background: isExpanded
                        ? `linear-gradient(90deg, ${accentColorVeryDark} 0%, ${accentColorDark} 50%, ${accentColor} 100%)`
                        : `linear-gradient(90deg, ${accentColorVeryDark} 0%, ${accentColorDark} 100%)`,
                }}
            >
                {/* Left edge highlight */}
                <div
                    className="absolute left-0 top-0 bottom-0 w-1 rounded-l"
                    style={{ backgroundColor: accentColor }}
                />

                {/* Main clickable area */}
                <button
                    onClick={onToggle}
                    className="flex-1 flex items-center gap-2 px-3 py-2.5 text-left min-w-0"
                >
                    <span className="text-white/60 text-xs flex-shrink-0">
                        {isExpanded ? '▼' : '▶'}
                    </span>
                    <span className="font-medium text-white truncate text-sm">
                        {book.title}
                    </span>
                    {/* Lore book indicator */}
                    {book.isLoreBook && (
                        <span className="text-amber-400 flex-shrink-0" title="Lore Book - Auto-generated from sub-hex lore">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                        </span>
                    )}
                </button>

                {/* Edit/Delete buttons - visible on hover (hidden for lore books) */}
                {canEdit && !book.isLoreBook && (
                    <div className="flex items-center gap-1 pr-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                            onClick={(e) => { e.stopPropagation(); onEditBook(); }}
                            className="p-1 text-white/50 hover:text-white transition-colors"
                            title="Edit book"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                        </button>
                        {!isOnlyBook && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onDeleteBook(); }}
                                className="p-1 text-white/50 hover:text-red-400 transition-colors"
                                title="Delete book"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Chapters - shown when book is expanded */}
            {isExpanded && (
                <div className="bg-slate-800/50 py-1">
                    {book.chapters.map(chapter => (
                        <ChapterItem
                            key={chapter.id}
                            chapter={chapter}
                            book={book}
                            hexId={hexId}
                            isExpanded={expandedChapters.has(chapter.id)}
                            onToggle={() => onToggleChapter(chapter.id)}
                            currentPageId={currentPageId}
                            onPageClick={onPageClick}
                            canEdit={canEdit}
                            onEdit={() => onEditChapter(chapter)}
                            onDelete={() => onDeleteChapter(chapter.id, chapter.title)}
                            onAddPage={() => onAddPage(chapter.id)}
                            pageHrefBuilder={pageHrefBuilder}
                        />
                    ))}

                    {/* Add Chapter button (hidden for lore books) */}
                    {canEdit && !book.isLoreBook && (
                        <button
                            onClick={onAddChapter}
                            className="w-full px-4 py-1.5 text-xs text-gray-500 hover:text-gray-300 hover:bg-slate-700/30 transition-colors text-left flex items-center gap-2"
                        >
                            <span>+</span>
                            <span>Add Chapter</span>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

interface ChapterItemProps {
    chapter: HexLibraryChapter;
    book: HexLibraryBook;
    hexId: number;
    isExpanded: boolean;
    onToggle: () => void;
    currentPageId: number | null;
    onPageClick: (pageId: number) => void;
    canEdit: boolean;
    onEdit: () => void;
    onDelete: () => void;
    onAddPage: () => void;
    /** Custom URL builder for page links */
    pageHrefBuilder?: (hexId: number, pageId: number) => string;
}

/**
 * ChapterItem - An expandable chapter within a book.
 */
function ChapterItem({
    chapter,
    book,
    hexId,
    isExpanded,
    onToggle,
    currentPageId,
    onPageClick,
    canEdit,
    onEdit,
    onDelete,
    onAddPage,
    pageHrefBuilder,
}: ChapterItemProps) {
    const hasPages = chapter.pages.length > 0;

    return (
        <div>
            {/* Chapter header */}
            <div className="group flex items-center hover:bg-slate-700/30 transition-colors">
                <button
                    onClick={onToggle}
                    className="flex-1 flex items-center gap-2 px-4 py-1.5 text-left min-w-0"
                >
                    <span className="text-gray-500 text-xs flex-shrink-0">
                        {hasPages ? (isExpanded ? '▼' : '▶') : '○'}
                    </span>
                    <span className="text-gray-300 text-sm truncate">
                        {chapter.title}
                    </span>
                    {hasPages && (
                        <span className="text-gray-500 text-xs flex-shrink-0">
                            ({chapter.pages.length})
                        </span>
                    )}
                </button>

                {/* Edit/Delete buttons (hidden for lore books) */}
                {canEdit && !book.isLoreBook && (
                    <div className="flex items-center gap-1 pr-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                            onClick={(e) => { e.stopPropagation(); onEdit(); }}
                            className="p-1 text-gray-500 hover:text-gray-300 transition-colors"
                            title="Edit chapter"
                        >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                        </button>
                        {book.chapters.length > 1 && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                                className="p-1 text-gray-500 hover:text-red-400 transition-colors"
                                title="Delete chapter"
                            >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Pages - shown when chapter is expanded */}
            {isExpanded && (
                <div className="pl-6">
                    {chapter.pages.map(page => (
                        <PageItem
                            key={page.id}
                            page={page}
                            hexId={hexId}
                            isActive={currentPageId === page.id}
                            onClick={() => onPageClick(page.id)}
                            pageHrefBuilder={pageHrefBuilder}
                        />
                    ))}

                    {/* Add Page button (hidden for lore books - lore pages are managed via sub-hex map) */}
                    {canEdit && !book.isLoreBook && (
                        <button
                            onClick={onAddPage}
                            className="w-full px-4 py-1 text-xs text-gray-500 hover:text-gray-300 hover:bg-slate-700/30 transition-colors text-left flex items-center gap-2"
                        >
                            <span>+</span>
                            <span>Add Page</span>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

interface PageItemProps {
    page: HexLibraryPage;
    hexId: number;
    isActive: boolean;
    onClick: () => void;
    /** Custom URL builder for page links */
    pageHrefBuilder?: (hexId: number, pageId: number) => string;
}

/**
 * PageItem - A clickable page link.
 */
function PageItem({ page, hexId, isActive, onClick, pageHrefBuilder }: PageItemProps) {
    const href = pageHrefBuilder
        ? pageHrefBuilder(hexId, page.id)
        : `/hex/${hexId}?tab=library&page=${page.id}`;

    return (
        <Link
            href={href}
            onClick={onClick}
            className={`block px-4 py-1 text-sm truncate transition-colors cursor-pointer ${
                isActive
                    ? 'text-purple-300 bg-purple-600/20'
                    : 'text-gray-400 hover:text-gray-200 hover:bg-slate-700/30'
            }`}
        >
            {page.name}
        </Link>
    );
}
